import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api, type PageResult } from '@/lib/api'

/**
 * Trending News (spec Requirement 6). Shapes follow `trending_items` and
 * `trending_item_locales`, which are the authoritative field list. The
 * prototype's "Trending value" and "Link URL" (spec Q22) are both real
 * columns now — `link_url` is staff-entered, not fetched from the page.
 */

/** Matches cwc-frontend/i18n/locales. English is required; the rest fall back to it. */
export const LOCALES = [
  { code: 'en', label: 'English', required: true },
  { code: 'zh-tw', label: '繁體中文', required: false },
  { code: 'ko', label: '한국어', required: false },
  { code: 'ja', label: '日本語', required: false },
] as const

export type LocaleCode = (typeof LOCALES)[number]['code']

const optionalLocale = z.object({
  title: z.string().max(200, 'Titles are capped at 200 characters.'),
  body: z.string(),
})

/**
 * The four locales are fixed by the spec, so this is an explicit object rather
 * than the open map the contract shows. Same wire shape, but it lets the
 * English-required rule sit on the field itself — a record-level refinement
 * reports against the whole object, which left the message with nowhere sensible
 * to render and the operator staring at a submit that silently did nothing.
 */
const localesSchema = z.object({
  en: z.object({
    // English is mandatory because every other locale falls back to it; without
    // it a member in an untranslated locale would see an empty entry.
    title: z
      .string()
      .trim()
      .min(1, 'An English title is required.')
      .max(200, 'Titles are capped at 200 characters.'),
    body: z.string(),
  }),
  // Optional keys, not empty strings: an untranslated locale has no row in
  // trending_item_locales at all, and a blank row would make the consumer
  // site render an empty title instead of falling back to English.
  'zh-tw': optionalLocale.optional(),
  ko: optionalLocale.optional(),
  ja: optionalLocale.optional(),
})

export const trendingSchema = z.object({
  /**
   * The row's place in a dense 1..N ordering — 1 is first, 76 is the 76th entry.
   *
   * Optional because it is **absent on create**: the server assigns MAX+1 so a
   * new entry lands last, and a client-supplied number would tear the sequence.
   * On edit it is present, and the server treats it as a *swap* — this row and
   * whatever sits at the target trade places, and no other row renumbers.
   *
   * There is no upper bound here on purpose. Only the server knows N, so it
   * clamps; anything the operator types above the list length lands at the end.
   *
   * Console ordering only. The consumer feed sorts by trending_value and never
   * sees this.
   */
  position: z.coerce
    .number()
    .int('Whole numbers only.')
    .min(1, 'The first row is position 1.')
    .optional(),
  /** 0-10000 — the score the consumer feed orders by. Editable any time. */
  trending_value: z.coerce
    .number()
    .int('Whole numbers only.')
    .min(0, 'Cannot be negative.')
    .max(10000, 'Keep the trending value at or under 10000.'),
  cover_url: z.string().min(1, 'A cover image is required.'),
  link_url: z.string().trim().url('Enter a valid URL, including https://.'),
  status: z.enum(['draft', 'published']),
  locales: localesSchema,
})

export type TrendingInput = z.infer<typeof trendingSchema>

export interface TrendingItem extends TrendingInput {
  id: number
  /**
   * Narrowed to required: only the create *input* omits position. Every
   * persisted row has one, so the list and editor can rely on it.
   */
  position: number
  created_at: string
  updated_at: string | null
}

/** Row shape for the list — the contract omitted it, so it is documented here. */
export interface TrendingRow extends TrendingItem {
  /** Locale codes that carry a non-empty title, for the "Locales" column. */
  filled_locales: LocaleCode[]
}

export type TrendingSort = 'position' | 'value' | 'recent'

/** Labels for the console's sort control. Server-side — see useTrendingList. */
export const TRENDING_SORTS: { value: TrendingSort; label: string }[] = [
  { value: 'position', label: 'Position' },
  { value: 'value', label: 'Trending value' },
  { value: 'recent', label: 'Newest first' },
]

const LIST_KEY = 'trending-news'

export function useTrendingList({
  page = 1,
  pageSize = 20,
  status,
  sort = 'position',
}: {
  page?: number
  pageSize?: number
  status?: 'draft' | 'published'
  sort?: TrendingSort
} = {}) {
  return useQuery({
    queryKey: [LIST_KEY, page, pageSize, status ?? 'all', sort],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        sort,
      })
      if (status) params.set('status', status)
      return api.get<PageResult<TrendingRow>>(`/admin/trending-news?${params}`)
    },
  })
}

/**
 * Both writes return the **full row**, not just an id.
 *
 * This was typed `{ id: number }` until 2026-08-19, which threw away the one
 * field the operator cannot predict: the position the server actually assigned.
 * A create lands at MAX+1, and a PATCH position is clamped to the list length —
 * so the returned `position` is authoritative and may differ from what was sent.
 * The caller needs it to say where the entry went.
 */
export function useSaveTrending() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: TrendingInput }) =>
      id === undefined
        ? api.post<TrendingItem>('/admin/trending-news', input)
        : api.patch<TrendingItem>(`/admin/trending-news/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
  })
}

export function useDeleteTrending() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<null>(`/admin/trending-news/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
  })
}

/**
 * Publish and unpublish are the same PATCH with a different status. Kept
 * separate from the editor so the table's inline toggle does not have to send
 * the whole record back.
 */
export function useSetTrendingStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: TrendingInput['status'] }) =>
      api.patch<TrendingItem>(`/admin/trending-news/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
  })
}
